import { createAction, handleActions } from 'redux-actions';

import { Plan } from '@api/types';
import { PlanPeriod } from './interfaces';

export interface PricingPlans {
  isError: boolean;
  isLoading: boolean;
  isDataLoaded: boolean;
  plans: Plan[];
  currentPlan?: Plan;
  canSwitchPeriod: boolean;
  planPeriod: PlanPeriod;
}

export const initialState: PricingPlans = {
  isError: false,
  isLoading: true,
  isDataLoaded: false,
  plans: [],
  canSwitchPeriod: false,
  planPeriod: PlanPeriod.Yearly,
};

// Selectors

export const getState = (state): PricingPlans => state?.pricingPlan?.pricingPlans;

export const getPlans = (state): Plan[] => getState(state)?.plans;

export const getCurrentPlan = (state): Plan | undefined => getState(state)?.currentPlan;

export const getMonthlyPlans = (state): Plan[] => getPlans(state).filter(plan => !plan.yearly);

export const getYearlyPlans = (state): Plan[] => getPlans(state).filter(plan => plan.yearly);

export const isYearly = (state): boolean => {
  const { canSwitchPeriod, planPeriod } = getState(state);

  return canSwitchPeriod && planPeriod === PlanPeriod.Yearly;
};

// Actions

export const LOAD = 'account/pricingPlan/pricingPlans/LOAD';
export const FETCH_DATA_STARTED = 'account/pricingPlan/pricingPlans/DATA_FETCH_STARTED';
export const FETCH_DATA_SUCCEEDED = 'account/pricingPlan/pricingPlans/DATA_FETCH_SUCCEEDED';
export const FETCH_DATA_FAILED = 'account/pricingPlan/pricingPlans/FETCH_DATA_FAILED';

export const SWITCH_PERIOD = 'account/pricingPlan/pricingPlans/SWITCH_PERIOD';

export const CANCEL_DOWNGRADE = 'account/pricingPlan/pricingPlans/CANCEL_DOWNGRADE';

// Action creators

export const load = createAction(LOAD);
export const fetchData = createAction(FETCH_DATA_STARTED);
export const fetchDataSucceeded = createAction(FETCH_DATA_SUCCEEDED);
export const fetchDataFailed = createAction(FETCH_DATA_FAILED);

export const switchPeriod = createAction(SWITCH_PERIOD);

export const cancelDowngrade = createAction(CANCEL_DOWNGRADE);

// Reducers

const handleFetchStarted = (state: PricingPlans) => ({
  ...state,
  isLoading: true,
});

const handleFetchFailed = (state: PricingPlans) => ({
  ...state,
  isLoading: false,
  isDataLoaded: false,
  isError: true,
});

const handleFetchSucceeded = (
  state: PricingPlans,
  { payload: { plans, currentPlan, canSwitchPeriod, planPeriod } },
) => ({
  ...state,
  plans,
  currentPlan,
  canSwitchPeriod,
  planPeriod,
  isLoading: false,
  isDataLoaded: true,
  isError: false,
});

const handleSwitchPeriod = (state: PricingPlans, { payload }) => ({
  ...state,
  planPeriod: payload,
});

export default handleActions(
  {
    [FETCH_DATA_STARTED]: handleFetchStarted,
    [FETCH_DATA_SUCCEEDED]: handleFetchSucceeded,
    [FETCH_DATA_FAILED]: handleFetchFailed,
    [SWITCH_PERIOD]: handleSwitchPeriod,
  },
  initialState,
);
